import { useState, useEffect, useCallback } from 'react';
import { FormStatus } from '../util/app.types';
import { extractSearchParams } from '../util/utils';
import { redeemToken } from '../api';
import { RedeemTokenTypes } from '../views/RedeemToken';

export enum PWResetParamStatus {
  InvalidParams = 'InvalidParams',
  ValidParams = 'ValidParams',
}

export enum ValidationCode {
  reuse = 'reuse',
  length = 'length',
  digit = 'digit',
  mixedcase = 'mixed.case',
  expiredNewEmail = 'expired.new.email',
}

export type ValidationMsg = {
  code: ValidationCode;
  extra: string;
};

const isKnownValidationCode = (str: string) =>
  Object.values<string>(ValidationCode).includes(str);

export const useRedeemToken = (
  searchParams: URLSearchParams,
  tokenType: RedeemTokenTypes,
) => {
  const [paramStatus, setParamStatus] = useState(
    PWResetParamStatus.ValidParams,
  );
  const [formStatus, setFormStatus] = useState(FormStatus.Editing);
  const [validationMessage, setValidationMessage] =
    useState<ValidationMsg | null>(null);

  useEffect(() => {
    const { email, token } = extractSearchParams(searchParams);
    if (!token || !email) {
      setParamStatus(PWResetParamStatus.InvalidParams);
    }
  }, [searchParams]);

  const dispatch = useCallback(
    async (pw: string) => {
      if (paramStatus === PWResetParamStatus.InvalidParams) {
        setFormStatus(FormStatus.Failed);
        return;
      }

      setFormStatus(FormStatus.InFlight);

      const { email, token } = extractSearchParams(searchParams);

      if (!token || !email) {
        return;
      }

      try {
        const response = await redeemToken(tokenType, token, email, pw);
        if (response.status >= 500) {
          // 5xx means server down or another serious issue.
          setFormStatus(FormStatus.Failed);
          return;
        }
        const json = await response.json();
        if (json?.success) {
          setFormStatus(FormStatus.Succeeded);
        } else {
          // unsuccessful, parse returned message
          const errorMessage = JSON.parse(json.message)[0];
          if (isKnownValidationCode(errorMessage.code)) {
            if (errorMessage.code === ValidationCode.expiredNewEmail) {
              setFormStatus(FormStatus.ExpiredNewUserToken);
            } else {
              setValidationMessage({
                code: errorMessage.code,
                extra: String(errorMessage.extra || 0),
              });
              // We are likely already in this state but let's make sure
              setFormStatus(FormStatus.Editing);
            }
          } else {
            // no error code means some kind of invalid token or request, usually due to the token not existing
            // (other cases shouldn't happen in normal operation, and we don't really care what error happens
            // if a user was trying to hack/break things)
            setFormStatus(FormStatus.InvalidToken);
          }
        }
      } catch (e) {
        // Server might be down for maintenance, etc.
        setFormStatus(FormStatus.Failed);
      }
    },
    [searchParams, paramStatus, tokenType],
  );

  return { formStatus, dispatch, validationMessage };
};
